import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { useThemeContext } from '../../contexts/ThemeContext';
import {
  Box,
  Button,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Tooltip,
  Typography,
  TextField,
  Stack,
} from '@mui/material';
import BlockRoundedIcon from '@mui/icons-material/BlockRounded';
import EditCalendarRoundedIcon from '@mui/icons-material/EditCalendarRounded';
import EventAvailableRoundedIcon from '@mui/icons-material/EventAvailableRounded';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import allLocales from '@fullcalendar/core/locales-all';
import type { DateSelectArg, EventClickArg } from '@fullcalendar/core';
import { formatInTimeZone, zonedTimeToUtc } from 'date-fns-tz';
import { CalendarEvent, CalendarEventSource } from '../../models/CalendarEvent';
import { apiClient, HOST_RETENTION_EVENTS } from '../../publicApiClient';
import { hostApiClient } from '../../hostApiClient';
import { tutorApiClient } from '../../tutorApiClient';
import { Space } from '../../models/Space';
import { Room } from '../../models/Room';
import type { BookingSlotsConfig } from '../../models/Room';
import { AdditionalGear, RequestedAdditionalGear } from '../../models/AdditionalGear';
import {
  HostTutorResponse,
  TutorLessonOption,
} from '../../models/Tutor';
import type { HostBookingClientOption } from '../../models/HostOrganizationUser';
import {
  BookingDialog,
  type HostCreateEventKind,
  type HostCreateIntent,
} from './components/BookingDialog';
import CalendarColorPicker from '../../components/CalendarColorPicker';
import HostCalendarScopeBar from '../../components/host/HostCalendarScopeBar';
import ManualBookingLinkDialog from '../../components/host/ManualBookingLinkDialog';
import RoomCalendarEventDetailsDialog from '../../components/host/RoomCalendarEventDetailsDialog';
import TutorCalendarEventDetailsDialog from '../../components/tutor/TutorCalendarEventDetailsDialog';
import { sharedCalendarCompactStyles } from '../../styles/sharedCalendarStyles';
import { calendarOverlayCompactStyles } from '../../styles/calendarOverlayStyles';
import { tutorCalendarCompactStyles, tutorCalendarStyles } from '../../styles/tutorCalendarStyles';
import {
  parseBookingCalendarInstant,
  serializeBookingWallClockForApi,
} from '../../utils/bookingInstant';
import { PaymentMethod, type DiscountSettings } from '../../models/Organization';
import {
  buildHostTutorWalkInBookingRequest,
  validateHostTutorWalkInBooking,
} from '../../utils/hostTutorWalkInBooking';
import {
  bookingSessionsOverlap,
  buildRoomBookingOccurrences,
  buildTutorBookingOccurrences,
} from '../../utils/bookingRepeatSlots';
import {
  buildBlockedSlotOverlayEvents,
  buildDayOffOverlayEvents,
  buildPastBlockedOverlayEvents,
  buildWorkingHoursOverlayEvents,
  calculateScheduleTimeRange,
  mountPastEventClass,
  withPastEventClassNames,
} from '../../utils/calendarScheduleOverlays';
import {
  collectTutorLessonRoomCalendarEventIds,
  enrichTutorEventRoomFromList,
  formatHostUnifiedTutorEventTitle,
  generateTutorColor,
  loadSpaceTutorCalendarEventsForRange,
  resolveHostTutorEventColor,
  TutorCalendarLessonEvent,
  TUTOR_IDENTITY_COLORS,
  tutorLessonClassName,
} from '../../utils/tutorCalendarEvents';
import {
  buildDurationOptionsMinutes,
  filterLessonOptionsByBookingSlotsConfig,
  snapDurationToOptions,
} from '../../utils/bookingSlotsConfig';
import { getBookingSlotMismatchErrorMessage } from '../../utils/bookingSlotErrors';
import {
  buildManualGuestBookingFields,
  composeManualBookingPhoneNumber,
  DEFAULT_MANUAL_BOOKING_COUNTRY_CODE,
  hasManualBookingClient,
  isManualBookingNewClient,
  MANUAL_BOOKING_NEW_CLIENT,
  resolveManualBookingClientUserId,
  resolveManualBookingShareUrl,
} from '../../utils/manualHostBooking';
import { contrastTextForBackground } from '../../utils/colorContrast';
import { ROOM_CALENDAR_COLOR_OPTIONS, ROOM_CALENDAR_COLORS } from '../../utils/calendarColors';
import {
  calendarDayKeysInRange,
  hostCalendarScopeAll,
  hostCalendarScopeChooserVisible,
  hostCalendarScopesEqual,
  hostCalendarScopeShowsRoomEvent,
  hostCalendarScopeShowsTutorEvent,
  resolveHostCalendarScope,
  type HostCalendarScope,
} from '../../utils/hostCalendarScope';

// Fallback when a room has no saved color
const ROOM_COLORS = [...ROOM_CALENDAR_COLORS];
const TUTOR_COLOR_OPTIONS = TUTOR_IDENTITY_COLORS.map((color, index) => ({
  key: `tutor${index + 1}`,
  color,
}));
const HOST_CALENDAR_CORNER_RADIUS = '8px';
const HOST_CALENDAR_ACCENT = '#4E67EB';
const HOST_CALENDAR_DAY_DOT_LIMIT = 4;

function fullCalendarHeaderDayKey(date: Date): string {
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${date.getUTCFullYear()}-${month}-${day}`;
}

function pushDayHeaderDot(dotsByDay: Map<string, string[]>, start: string, color: string) {
  const day = start.slice(0, 10);
  const dots = dotsByDay.get(day) ?? [];
  if (dots.length >= HOST_CALENDAR_DAY_DOT_LIMIT) return;
  dots.push(color);
  dotsByDay.set(day, dots);
}

function withCurrentCalendarColor(
  options: readonly { key?: string; color: string }[],
  current: string,
): { key?: string; color: string }[] {
  if (options.some((option) => option.color.toLowerCase() === current.toLowerCase())) {
    return [...options];
  }
  return [{ color: current }, ...options];
}

// Generate color from room - use assigned color if available, otherwise fallback to auto-generated
const generateRoomColor = (roomId: string | undefined, rooms: Room[]): string => {
  if (!roomId) {
    // fallback color if roomId is missing
    return ROOM_COLORS[5] ?? '#1E88E5';
  }
  // Find the room and use its assigned color if available
  const room = rooms.find(room => room.id === roomId);
  if (room?.color) {
    return room.color;
  }
  // Fallback to auto-generated color based on room index
  const roomIndex = rooms.findIndex(room => room.id === roomId);
  const colorIndex = roomIndex >= 0 ? roomIndex % ROOM_COLORS.length : 0;
  return ROOM_COLORS[colorIndex];
};

function roomCalendarEventPassesDisplayRules(
  event: CalendarEvent,
  linkedRoomCalendarEventIds: Set<string>,
): boolean {
  const calendarEventId = event.id.includes('_merged_') ? event.id.split('_merged_')[0] : event.id;
  if (linkedRoomCalendarEventIds.has(calendarEventId)) return false;
  if ((event as { isManualBlock?: boolean }).isManualBlock) return true;
  if (
    event.source === CalendarEventSource.BOOKING ||
    event.source === CalendarEventSource.HOST_BOOKING ||
    event.source === CalendarEventSource.GOOGLE_CALENDAR_SYNCED_BOOKING
  ) {
    return true;
  }
  const title = event.title || '';
  if (!title.includes(':')) return false;
  const [roomName, description] = title.split(':', 2);
  return roomName.trim().length > 0 && description.trim().length > 0;
}

function tutorEventOccupiedRoomId(
  event: TutorCalendarLessonEvent,
  roomIdByCalendarEventId: ReadonlyMap<string, string>,
): string | undefined {
  const direct = event.roomId?.trim();
  if (direct) return direct;
  const linked = event.roomCalendarEventId?.trim();
  if (!linked) return undefined;
  return roomIdByCalendarEventId.get(linked);
}

// Merge events that occur at the same time slot for the same room
const mergeEventsAtSameTimeSlot = (
  events: (CalendarEvent & { roomId: string; roomName: string; roomIndex: number; isManualBlock?: boolean })[]
): (CalendarEvent & { roomId: string; roomName: string; roomIndex: number; isManualBlock?: boolean })[] => {
  const eventGroups = new Map<string, (CalendarEvent & { roomId: string; roomName: string; roomIndex: number; isManualBlock?: boolean })[]>();
  
  events.forEach(event => {
    const key = `${event.roomId}_${event.startTime}_${event.endTime}`;
    if (!eventGroups.has(key)) {
      eventGroups.set(key, []);
    }
    eventGroups.get(key)!.push(event);
  });
  
  // Merge events in each group
  const mergedEvents: (CalendarEvent & { roomId: string; roomName: string; roomIndex: number; isManualBlock?: boolean })[] = [];
  
  eventGroups.forEach((groupEvents, key) => {
    if (groupEvents.length === 1) {
      // No merging needed
      mergedEvents.push(groupEvents[0]);
      return;
    }
    
    // Find the first event with room name in title (BOOKING or HOST_BOOKING)
    const roomEvent = groupEvents.find(event => {
      const title = event.title || '';
      // Check if title contains room information (room name followed by colon)
      return title.includes(':') && 
             (event.source === CalendarEventSource.BOOKING || 
              event.source === CalendarEventSource.HOST_BOOKING);
    });
    
    // Other events are considered gear events
    const gearEvents = groupEvents.filter(event => event !== roomEvent);
    
    if (roomEvent) {
      // Merge: room event title + gear event titles
      const gearTitles = gearEvents
        .map(event => event.title)
        .filter(title => title && title.trim().length > 0);
      
      const mergedTitle = gearTitles.length > 0
        ? `${roomEvent.title} + ${gearTitles.join(', ')}`
        : roomEvent.title;
      
      // Create merged event using the room event as base
      mergedEvents.push({
        ...roomEvent,
        title: mergedTitle,
        // Combine IDs for tracking
        id: `${roomEvent.id}_merged_${gearEvents.map(e => e.id).join('_')}`,
      });
    } else {
      // If no room event found, just combine all titles
      const allTitles = groupEvents
        .map(event => event.title)
        .filter(title => title && title.trim().length > 0);
      
      const firstEvent = groupEvents[0];
      mergedEvents.push({
        ...firstEvent,
        title: allTitles.join(' + '),
        id: `merged_${groupEvents.map(e => e.id).join('_')}`,
      });
    }
  });
  
  return mergedEvents;
};

export default function SpaceCalendarPage() {
  const { t } = useTranslation();
  const { mode } = useThemeContext();
  const [searchParams] = useSearchParams();
  const spaceIdFromUrl = searchParams.get('spaceId')?.trim() || '';
  const dateFromUrl = searchParams.get('date')?.trim() || '';
  const calendarRef = useRef<FullCalendar>(null);
  const calendarContainerRef = useRef<HTMLDivElement | null>(null);
  
  // Helper function to map locale codes to FullCalendar format
  const getFullCalendarLocale = (locale: string) => {
    const localeMap: Record<string, string> = {
      'en': 'en',
      'pl': 'pl'
    };
    return localeMap[locale] || 'en';
  };

  const [spaces, setSpaces] = useState<Space[]>([]);
  const [selectedSpace, setSelectedSpace] = useState<Space | null>(null);
  const [rooms, setRooms] = useState<Room[]>([]);
  const [organizationBookingSlotsConfig, setOrganizationBookingSlotsConfig] =
    useState<BookingSlotsConfig | null>(null);
  const [organizationDiscountSettings, setOrganizationDiscountSettings] =
    useState<DiscountSettings | null>(null);
  const [calendarEvents, setCalendarEvents] = useState<CalendarEvent[]>([]);
  const [view, setView] = useState<'dayGridMonth' | 'timeGridDay' | 'timeGridWeek'>('timeGridWeek');
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [toolbarDate, setToolbarDate] = useState<Date>(() => new Date());
  const [selectedTime, setSelectedTime] = useState<Date | null>(null);
  const [extraSessionStarts, setExtraSessionStarts] = useState<Date[]>([]);
  const [selectedRoom, setSelectedRoom] = useState<string>('');
  const [bookingDuration, setBookingDuration] = useState<number>(60);
  const [isBookingDialogOpen, setIsBookingDialogOpen] = useState(false);
  const [createIntent, setCreateIntent] = useState<HostCreateIntent>('block');
  const [createEventKind, setCreateEventKind] = useState<HostCreateEventKind>('room');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
  const [availableGears, setAvailableGears] = useState<AdditionalGear[]>([]);
  const [selectedAdditionalGears, setSelectedAdditionalGears] = useState<RequestedAdditionalGear[]>([]);
  const [loadingGears, setLoadingGears] = useState(false);
  const [gearError, setGearError] = useState<string | null>(null);
  const [hasConflict, setHasConflict] = useState(false);
  const [isPastBooking, setIsPastBooking] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [isEventDetailsOpen, setIsEventDetailsOpen] = useState(false);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const [targetDate, setTargetDate] = useState<Date | null>(null);
  const [calendarScope, setCalendarScope] = useState<HostCalendarScope>(() => hostCalendarScopeAll());
  const [visibleDayKeys, setVisibleDayKeys] = useState<string[]>([]);
  const calendarScopeSpaceId = useRef<string | undefined>(undefined);
  const [spaceTutors, setSpaceTutors] = useState<HostTutorResponse[]>([]);
  const [orgHasTutors, setOrgHasTutors] = useState(false);
  const [tutorEvents, setTutorEvents] = useState<TutorCalendarLessonEvent[]>([]);
  const [selectedTutorEvent, setSelectedTutorEvent] = useState<TutorCalendarLessonEvent | null>(null);
  const [isTutorEventDetailsOpen, setIsTutorEventDetailsOpen] = useState(false);
  const [deleteTutorBlockDialogOpen, setDeleteTutorBlockDialogOpen] = useState(false);
  const [deletingTutorBlock, setDeletingTutorBlock] = useState(false);
  const [deleteRoomBlockDialogOpen, setDeleteRoomBlockDialogOpen] = useState(false);
  const [deletingRoomBlock, setDeletingRoomBlock] = useState(false);
  const [actionTutorId, setActionTutorId] = useState('');
  const [submittingCreateEvent, setSubmittingCreateEvent] = useState(false);
  const [tutorLessonOptionId, setTutorLessonOptionId] = useState('');
  const [tutorTitle, setTutorTitle] = useState('');
  const [tutorStudentId, setTutorStudentId] = useState(MANUAL_BOOKING_NEW_CLIENT);
  const [guestPhoneLocal, setGuestPhoneLocal] = useState('');
  const [sendBookingLinkSms, setSendBookingLinkSms] = useState(true);
  const [organizationStudents, setOrganizationStudents] = useState<HostBookingClientOption[]>([]);
  const [organizationStudentsLoading, setOrganizationStudentsLoading] = useState(false);
  const [bookingLinkDialogOpen, setBookingLinkDialogOpen] = useState(false);
  const [createdBookingLinkUrl, setCreatedBookingLinkUrl] = useState<string | null>(null);
  const [bookingLinkCopied, setBookingLinkCopied] = useState(false);

  // Helper: parse FullCalendar ISO (in calendar tz) into a local Date preserving wall-time
  const parseIsoToLocalDate = (isoString: string): Date => {
    // Expected formats: 'yyyy-MM-ddTHH:mm:ss' or 'yyyy-MM-dd'
    const [datePart, timePart] = isoString.split('T');
    const [year, month, day] = datePart.split('-').map(Number);
    if (timePart) {
      const [hourStr, minuteStr] = timePart.split(':');
      const hours = Number(hourStr);
      const minutes = Number(minuteStr);
      return new Date(year, month - 1, day, hours, minutes, 0, 0);
    }
    return new Date(Number(year), Number(month) - 1, Number(day));
  };

  // Keep FullCalendar sized when the host nav drawer opens/closes (not only on window resize).
  useEffect(() => {
    const updateCalendarSize = () => {
      calendarRef.current?.getApi()?.updateSize();
    };

    window.addEventListener('resize', updateCalendarSize);

    const container = calendarContainerRef.current;
    let resizeObserver: ResizeObserver | null = null;
    let debounceTimer: number | undefined;

    if (container && typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => {
        // Drawer transitions change width over time — debounce + follow-up pass.
        window.clearTimeout(debounceTimer);
        updateCalendarSize();
        debounceTimer = window.setTimeout(updateCalendarSize, 220);
      });
      resizeObserver.observe(container);
    }

    updateCalendarSize();

    return () => {
      window.removeEventListener('resize', updateCalendarSize);
      window.clearTimeout(debounceTimer);
      resizeObserver?.disconnect();
    };
  }, [selectedSpace?.id]);

  // Remove old CSS injection that forced white text in dark mode
  useEffect(() => {
    const styleId = 'fullcalendar-dark-mode-override';
    const existingStyle = document.getElementById(styleId);
    if (existingStyle) existingStyle.remove();
  }, [mode]);

  // Handle view changes
  useEffect(() => {
    const api = calendarRef.current?.getApi();
    if (api) {
      api.updateSize();
      
      // If we have a target date, navigate to it
      if (targetDate) {
        api.gotoDate(targetDate);
        setTargetDate(null); // Clear the target date after navigation
      }
    }
  }, [view, targetDate]);


  // Fetch user's spaces once on mount
  useEffect(() => {
    hostApiClient.getSpaces()
      .then((fetchedSpaces) => {
        setSpaces(fetchedSpaces);
        setSelectedSpace((prev) => prev ?? fetchedSpaces[0] ?? null);
      })
      .catch(console.error);
  }, []);

  // Deep link: ?spaceId= preselects the matching space
  useEffect(() => {
    if (!spaceIdFromUrl || spaces.length === 0) return;
    const fromUrl = spaces.find((space) => space.id === spaceIdFromUrl);
    if (fromUrl) {
      setSelectedSpace(fromUrl);
    }
  }, [spaceIdFromUrl, spaces]);

  // Deep link: ?date=yyyy-MM-dd jumps the calendar to that day
  useEffect(() => {
    if (!dateFromUrl || !selectedSpace?.id) return;
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateFromUrl);
    if (!match) return;
    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    if (!year || !month || !day) return;
    setTargetDate(new Date(year, month - 1, day));
  }, [dateFromUrl, selectedSpace?.id]);

  // Fetch rooms when space is selected
  useEffect(() => {
    if (!selectedSpace?.id) {
      setRooms([]);
      setCalendarEvents([]);
      setTutorEvents([]);
      return;
    }
    
    hostApiClient.getRooms(selectedSpace.id)
      .then(setRooms)
      .catch(console.error);
  }, [selectedSpace?.id]);

  useEffect(() => {
    let cancelled = false;
    hostApiClient
      .getOrganization()
      .then((org) => {
        if (!cancelled) {
          setOrganizationBookingSlotsConfig(org.bookingSlotsConfig ?? null);
          setOrganizationDiscountSettings(org.discountSettings ?? null);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setOrganizationBookingSlotsConfig(null);
          setOrganizationDiscountSettings(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Ensure we have the full weekly schedule for slot clipping (list payload can be stale/partial).
  useEffect(() => {
    if (!selectedSpace?.id) return;
    let cancelled = false;
    hostApiClient
      .getSpace(selectedSpace.id)
      .then((detail) => {
        if (cancelled || !detail?.space?.weeklySchedule) return;
        const nextSchedule = detail.space.weeklySchedule;
        const nextTimezone = detail.space.timezone;
        setSelectedSpace((prev) => {
          if (!prev || prev.id !== detail.space.id) return prev;
          const sameSchedule =
            JSON.stringify(prev.weeklySchedule) === JSON.stringify(nextSchedule);
          const sameTimezone = !nextTimezone || prev.timezone === nextTimezone;
          if (sameSchedule && sameTimezone) return prev;
          return {
            ...prev,
            weeklySchedule: nextSchedule,
            timezone: nextTimezone || prev.timezone,
          };
        });
        setSpaces((prev) =>
          prev.map((space) =>
            space.id === detail.space.id
              ? {
                  ...space,
                  weeklySchedule: nextSchedule,
                  timezone: nextTimezone || space.timezone,
                }
              : space,
          ),
        );
      })
      .catch(() => {
        // Keep list payload schedule if detail fetch fails.
      });
    return () => {
      cancelled = true;
    };
  }, [selectedSpace?.id]);

  // Fetch org tutors filtered to the selected space
  useEffect(() => {
    if (!selectedSpace?.id) {
      setSpaceTutors([]);
      setActionTutorId('');
      return;
    }

    const spaceId = selectedSpace.id;
    let cancelled = false;
    tutorApiClient
      .getMyTutors()
      .then((rows) => {
        if (cancelled) return;
        setOrgHasTutors(rows.length > 0);
        const forSpace = rows.filter((row) => row.spaceId === spaceId);
        setSpaceTutors(forSpace);
        setActionTutorId((current) =>
          forSpace.some((row) => row.tutorId === current)
            ? current
            : forSpace[0]?.tutorId ?? '',
        );
      })
      .catch(() => {
        if (!cancelled) {
          // Keep last known orgHasTutors — visibility is non-critical on transient errors.
          setSpaceTutors([]);
          setActionTutorId('');
        }
      });

    return () => {
      cancelled = true;
    };
  }, [selectedSpace?.id]);
  const spaceTutorOptions = useMemo(
    () =>
      spaceTutors.map((row) => ({
        tutorId: row.tutorId,
        name: row.name?.trim() || t('tutorCalendar.unnamedTutor', 'Tutor'),
        timezone: row.timezone,
        color: row.color ?? null,
      })),
    [spaceTutors, t],
  );

  useEffect(() => {
    const spaceChanged = calendarScopeSpaceId.current !== selectedSpace?.id;
    calendarScopeSpaceId.current = selectedSpace?.id;
    const roomIds = rooms.map((room) => room.id);
    const tutorIds = orgHasTutors ? spaceTutorOptions.map((tutor) => tutor.tutorId) : [];
    setCalendarScope((current) => {
      const base = spaceChanged ? hostCalendarScopeAll() : current;
      const next = resolveHostCalendarScope({
        current: base,
        roomIds,
        tutorIds,
      });
      return hostCalendarScopesEqual(current, next) ? current : next;
    });
  }, [orgHasTutors, rooms, selectedSpace?.id, spaceTutorOptions]);

  const actionTutor = useMemo(
    () => spaceTutors.find((row) => row.tutorId === actionTutorId) ?? null,
    [spaceTutors, actionTutorId],
  );

  const tutorLessonOptions: TutorLessonOption[] = filterLessonOptionsByBookingSlotsConfig(
    actionTutor?.lessonOptions ?? [],
    actionTutor?.bookingSlotsConfig,
  );

  // Fetch calendar events for all rooms + space tutors.
  // Room and tutor loads are independent so one failure cannot wipe the other.
  // Do not clear existing events when the calendar API is briefly unavailable
  // (e.g. FullCalendar remount) — that race left the grid empty after create.
  const fetchEvents = useCallback(async () => {
    const calendarApi = calendarRef.current?.getApi();
    if (!calendarApi || !selectedSpace) {
      return;
    }

    const start = calendarApi.view.activeStart;
    const end = calendarApi.view.activeEnd;
    const startUTC = start.toISOString().replace('Z', '');
    const endUTC = end.toISOString().replace('Z', '');

    type RoomCalendarRow = CalendarEvent & {
      roomId: string;
      roomName: string;
      roomIndex: number;
      isManualBlock?: boolean;
    };

    const loadRoomEvents = async (): Promise<RoomCalendarRow[]> => {
      if (rooms.length === 0) return [];
      const roomEvents = await Promise.all(
        rooms.map(async (room, roomIndex) => {
          const [generalEvents, hostManualEvents] = await Promise.all([
            apiClient.getRoomCalendarEvents(room.id, startUTC, endUTC),
            hostApiClient
              .getHostRoomCalendarEvents(room.id, startUTC, endUTC)
              .catch(() => [] as CalendarEvent[]),
          ]);
          const manualKeys = new Set(hostManualEvents.map((e) => `${e.startTime}_${e.endTime}`));
          const generalFiltered = generalEvents.filter((e) => {
            const key = `${e.startTime}_${e.endTime}`;
            return !(e.source === CalendarEventSource.HOST_BOOKING && manualKeys.has(key));
          });
          const withRoom = (e: CalendarEvent, isManual = false) => ({
            ...e,
            roomId: room.id,
            roomName: room.name,
            roomIndex,
            ...(isManual && { isManualBlock: true as const }),
          });
          return [
            ...generalFiltered.map((e) => withRoom(e)),
            ...hostManualEvents.map((e) => withRoom(e, true)),
          ];
        }),
      );
      const allEvents: RoomCalendarRow[] = [];
      roomEvents.forEach((events) => allEvents.push(...events));
      return mergeEventsAtSameTimeSlot(allEvents);
    };

    const loadTutorEvents = async (): Promise<TutorCalendarLessonEvent[]> => {
      if (spaceTutorOptions.length === 0) return [];
      return loadSpaceTutorCalendarEventsForRange(
        spaceTutorOptions,
        start,
        end,
        selectedSpace.timezone,
        hostApiClient.getHostTutorBookings.bind(hostApiClient),
        t('tutorCalendar.externalBlock', 'Google Calendar'),
        t('tutorCalendar.lessonFallback', 'Lesson'),
        t('tutorCalendar.walkInStudentLabel', 'Walk-in'),
      );
    };

    const [roomResult, tutorResult] = await Promise.allSettled([loadRoomEvents(), loadTutorEvents()]);

    if (roomResult.status === 'fulfilled') {
      setCalendarEvents(roomResult.value);
    } else {
      console.error(t('spaceCalendar.errorFetchingEvents', 'Error fetching calendar events:'), roomResult.reason);
    }

    if (tutorResult.status === 'fulfilled') {
      setTutorEvents(tutorResult.value);
    } else {
      console.error(t('spaceCalendar.errorFetchingEvents', 'Error fetching calendar events:'), tutorResult.reason);
    }
  }, [rooms, selectedSpace?.id, selectedSpace?.timezone, spaceTutorOptions, t]);

  // Refetch when rooms / tutors / space change. Date navigation is handled by datesSet.
  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);
  const workingHoursEvents = useMemo(
    () =>
      buildWorkingHoursOverlayEvents(
        selectedSpace?.weeklySchedule,
        toolbarDate,
        selectedSpace?.timezone ?? '',
      ),
    [selectedSpace?.timezone, selectedSpace?.weeklySchedule, toolbarDate],
  );

  const blockedSlotEvents = useMemo(
    () =>
      buildBlockedSlotOverlayEvents(
        selectedSpace?.weeklySchedule,
        toolbarDate,
        selectedSpace?.timezone ?? '',
      ),
    [selectedSpace?.timezone, selectedSpace?.weeklySchedule, toolbarDate],
  );

  const dayOffEvents = useMemo(
    () =>
      buildDayOffOverlayEvents(
        selectedSpace?.weeklySchedule,
        toolbarDate,
        selectedSpace?.timezone ?? '',
      ),
    [selectedSpace?.timezone, selectedSpace?.weeklySchedule, toolbarDate],
  );

  const pastBlockedEvents = useMemo(
    () => buildPastBlockedOverlayEvents(toolbarDate, selectedSpace?.timezone ?? ''),
    [selectedSpace?.timezone, toolbarDate],
  );

  // Visible hours = space opening hours only (no overnight/closed-hour slots).
  const timeRange = useMemo(
    () => calculateScheduleTimeRange(selectedSpace?.weeklySchedule),
    [selectedSpace?.weeklySchedule],
  );

  // Keep FullCalendar axis in sync when the space schedule changes.
  useEffect(() => {
    const api = calendarRef.current?.getApi();
    if (!api) return;
    api.setOption('slotMinTime', timeRange.slotMinTime);
    api.setOption('slotMaxTime', timeRange.slotMaxTime);
    api.setOption('scrollTime', timeRange.slotMinTime);
    api.updateSize();
  }, [timeRange.slotMinTime, timeRange.slotMaxTime, selectedSpace?.id]);

  const toolbarMonthYearLabel = useMemo(
    () =>
      toolbarDate
        .toLocaleDateString(t('calendar.locale', 'en'), { month: 'long', year: 'numeric' })
        .replace(/^\w/, (c: string) => c.toUpperCase()),
    [t, toolbarDate],
  );

  // Handle calendar navigation and view changes
  const handleDatesSet = useCallback((dateInfo: { start: Date; end: Date; startStr: string; endStr: string; timeZone: string; view: any }) => {
    // Keep toolbar month/year in sync with the visible range (not booking selection).
    const currentStart = dateInfo.view?.currentStart ?? dateInfo.start;
    setToolbarDate(currentStart instanceof Date ? currentStart : new Date(currentStart));
    setVisibleDayKeys(calendarDayKeysInRange(dateInfo.startStr, dateInfo.endStr));
    const nextView = dateInfo.view?.type;
    if (nextView === 'dayGridMonth' || nextView === 'timeGridWeek' || nextView === 'timeGridDay') {
      setView(nextView);
    }
    fetchEvents();
  }, [fetchEvents]);

  // FullCalendar does not always pick up customButtons.text from React props — push it via the API.
  useEffect(() => {
    const api = calendarRef.current?.getApi();
    if (!api) return;
    api.setOption('customButtons', {
      monthYearTitle: {
        text: toolbarMonthYearLabel,
        click: () => undefined,
      },
    });
  }, [toolbarMonthYearLabel, selectedSpace?.id]);

  // View and space change handlers

  const handleSpaceChange = (spaceId: string) => {
    const space = spaces.find(s => s.id === spaceId) || null;
    setSelectedSpace(space);
    setCalendarScope(hostCalendarScopeAll());
  };

  const handleRoomColorChange = async (roomId: string, color: string) => {
    const previous = rooms.find((room) => room.id === roomId)?.color;
    setRooms((prev) => prev.map((room) => (room.id === roomId ? { ...room, color } : room)));
    try {
      await hostApiClient.updateRoomColor(roomId, color);
    } catch (error) {
      console.error('Failed to update room color', error);
      setRooms((prev) =>
        prev.map((room) => (room.id === roomId ? { ...room, color: previous } : room)),
      );
    }
  };

  const handleTutorColorChange = async (tutorId: string, color: string) => {
    const previous = spaceTutors.find((tutor) => tutor.tutorId === tutorId)?.color;
    setSpaceTutors((prev) =>
      prev.map((tutor) => (tutor.tutorId === tutorId ? { ...tutor, color } : tutor)),
    );
    try {
      await hostApiClient.updateTutorColor(tutorId, color);
    } catch (error) {
      console.error('Failed to update tutor color', error);
      setSpaceTutors((prev) =>
        prev.map((tutor) => (tutor.tutorId === tutorId ? { ...tutor, color: previous } : tutor)),
      );
    }
  };

  const syncTutorLessonOptionForTutor = (tutorId: string) => {
    const tutor = spaceTutors.find((row) => row.tutorId === tutorId) ?? null;
    const firstOptionId = tutor?.lessonOptions?.find((option) => Boolean(option.id))?.id?.trim() || '';
    setTutorLessonOptionId(firstOptionId);
  };

  const loadBookingClients = useCallback(async (kind: HostCreateEventKind) => {
    setOrganizationStudentsLoading(true);
    try {
      const users =
        kind === 'tutor'
          ? await hostApiClient.getHostBookingClientsStudents()
          : await hostApiClient.getHostBookingClientsRoomRenters();
      setOrganizationStudents(users);
    } catch (error) {
      console.error('Error loading booking clients:', error);
      setOrganizationStudents([]);
    } finally {
      setOrganizationStudentsLoading(false);
    }
  }, []);

  const resolveManualGuestFields = () => {
    const userId = resolveManualBookingClientUserId(tutorStudentId);
    return buildManualGuestBookingFields({
      userId,
      phoneNumber: isManualBookingNewClient(tutorStudentId)
        ? composeManualBookingPhoneNumber(DEFAULT_MANUAL_BOOKING_COUNTRY_CODE, guestPhoneLocal)
        : null,
      sendSms: sendBookingLinkSms,
    });
  };

  const showManualBookingCreatedSuccess = (
    bookingLinkUrl: string | null | undefined,
    fallbackMessage: string,
  ) => {
    setSnackbarMessage(fallbackMessage);
    setSnackbarSeverity('success');
    setSnackbarOpen(true);
    const shareUrl = resolveManualBookingShareUrl(bookingLinkUrl);
    if (shareUrl) {
      setCreatedBookingLinkUrl(shareUrl);
      setBookingLinkCopied(false);
      setBookingLinkDialogOpen(true);
    }
  };

  const handleCopyBookingLink = async () => {
    if (!createdBookingLinkUrl) {
      return;
    }
    try {
      await navigator.clipboard.writeText(createdBookingLinkUrl);
      setBookingLinkCopied(true);
    } catch (error) {
      console.error('Failed to copy booking link:', error);
    }
  };

  const openCreateEventDialog = (
    intent: HostCreateIntent = 'block',
    kind: HostCreateEventKind = 'room',
    options?: { date?: Date | null; time?: Date | null; duration?: number },
  ) => {
    const resolvedKind: HostCreateEventKind =
      kind === 'tutor' && spaceTutors.length === 0 && rooms.length > 0
        ? 'room'
        : kind === 'room' && rooms.length === 0 && spaceTutors.length > 0
          ? 'tutor'
          : kind;
    setCreateIntent(intent);
    setCreateEventKind(resolvedKind);
    if (options?.date !== undefined) setSelectedDate(options.date);
    if (options?.time !== undefined) setSelectedTime(options.time);
    if (options?.duration !== undefined) setBookingDuration(options.duration);
    setTutorTitle('');
    setTutorStudentId(MANUAL_BOOKING_NEW_CLIENT);
    setGuestPhoneLocal('');
    setSendBookingLinkSms(true);
    setSelectedAdditionalGears([]);
    setAvailableGears([]);
    setGearError(null);
    setExtraSessionStarts([]);

    const roomId =
      selectedRoom && rooms.some((row) => row.id === selectedRoom)
        ? selectedRoom
        : rooms[0]?.id ?? '';
    setSelectedRoom(roomId);

    const tutorId =
      actionTutorId && spaceTutors.some((row) => row.tutorId === actionTutorId)
        ? actionTutorId
        : spaceTutors[0]?.tutorId ?? '';
    if (tutorId) {
      setActionTutorId(tutorId);
      syncTutorLessonOptionForTutor(tutorId);
    } else {
      setTutorLessonOptionId('');
    }

    if (intent === 'booking') {
      void loadBookingClients(resolvedKind);
    }

    setIsBookingDialogOpen(true);
  };

  const handleCreateEventKindChange = (kind: HostCreateEventKind) => {
    setCreateEventKind(kind);
    if (createIntent === 'booking') {
      setTutorStudentId(MANUAL_BOOKING_NEW_CLIENT);
      setGuestPhoneLocal('');
      void loadBookingClients(kind);
      if (!actionTutorId && spaceTutors[0]) {
        setActionTutorId(spaceTutors[0].tutorId);
        syncTutorLessonOptionForTutor(spaceTutors[0].tutorId);
      }
    }
  };

  const handleActionTutorChange = (tutorId: string) => {
    setActionTutorId(tutorId);
    syncTutorLessonOptionForTutor(tutorId);
  };

  // Date-related utilities
  const combineDateTime = (date: Date, time: Date): Date => {
    const combined = new Date(date);
    combined.setHours(time.getHours(), time.getMinutes(), 0, 0);
    return combined;
  };

  const formatTimeForApi = (dateTime: Date, timezone: string): string => {
    // Picker values are wall-clock digits; interpret them in the API timezone, never the device TZ.
    return serializeBookingWallClockForApi(dateTime, timezone);
  };

  const resolveTutorActionTimezone = (): string | undefined => {
    const tutorTz = actionTutor?.timezone?.trim();
    if (tutorTz) return tutorTz;
    const spaceTz = selectedSpace?.timezone?.trim();
    return spaceTz || undefined;
  };

  // Booking-related handlers
  const handleDateSelect = (selectInfo: DateSelectArg) => {
    console.log('handleDateSelect called with:', selectInfo);
    console.log('Selection start:', selectInfo.start);
    console.log('Selection end:', selectInfo.end);
    
    // Build local Date objects from the ISO strings to preserve displayed wall-time
    const startLocal = parseIsoToLocalDate(selectInfo.startStr);
    const endLocal = parseIsoToLocalDate(selectInfo.endStr);
    const now = new Date();
    
    // Prevent selecting dates in the past
    if (startLocal < now) {
      setSnackbarMessage(t('spaceCalendar.cannotSelectPastDate', 'Cannot select a date in the past'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }
    
    // Extract date and time parts for the form (local)
    const dateOnly = new Date(startLocal.getFullYear(), startLocal.getMonth(), startLocal.getDate());
    const timeOnly = new Date(startLocal);
    
    setSelectedDate(dateOnly);
    setSelectedTime(timeOnly);
    
    // Calculate duration from selection (in minutes)
    const durationMs = endLocal.getTime() - startLocal.getTime();
    const durationMinutes = Math.max(60, Math.round(durationMs / (1000 * 60))); // Minimum 1 hour
    setBookingDuration(durationMinutes);
    
    setSelectedAdditionalGears([]);
    setAvailableGears([]);
    setGearError(null);
    
    openCreateEventDialog('block', 'room', {
      date: dateOnly,
      time: timeOnly,
      duration: durationMinutes,
    });
  };

  // Date click handler for day cells
  const handleDateClick = (dateInfo: any) => {
    console.log('Date clicked:', dateInfo);
    console.log('Date info date:', dateInfo.date);
    console.log('Date info dateStr:', dateInfo.dateStr);
    
    const now = new Date();
    // Prefer the ISO string to preserve wall-time as displayed by the calendar timezone
    const preciseDateTime: Date = parseIsoToLocalDate(dateInfo.dateStr);
    const clickedDate = new Date(preciseDateTime.getFullYear(), preciseDateTime.getMonth(), preciseDateTime.getDate());
    
    if (dateInfo.view?.type === 'dayGridMonth') {
      calendarRef.current?.getApi().changeView('timeGridDay', preciseDateTime);
      return;
    }
    
    // For time-based views (weekly/daily), use the exact time clicked
    let selectedTime = new Date(preciseDateTime);
    
    console.log('Clicked date:', clickedDate);
    console.log('Selected time:', selectedTime);
    
    // Prevent selecting times in the past (use precise time)
    if (selectedTime < now) {
      setSnackbarMessage(t('spaceCalendar.cannotSelectPastDate', 'Cannot select a date in the past'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }
    
    setSelectedDate(clickedDate);
    setSelectedTime(selectedTime);
    setBookingDuration(60); // Default 1 hour
    
    setSelectedAdditionalGears([]);
    setAvailableGears([]);
    setGearError(null);
    
    openCreateEventDialog('block', 'room', {
      date: clickedDate,
      time: selectedTime,
      duration: 60,
    });
  };

  const handleCloseBookingDialog = () => {
    setIsBookingDialogOpen(false);
    setExtraSessionStarts([]);
    setTutorTitle('');
    setTutorStudentId(MANUAL_BOOKING_NEW_CLIENT);
    setGuestPhoneLocal('');
    setSendBookingLinkSms(true);
    setSelectedAdditionalGears([]);
    setAvailableGears([]);
  };

  const handleSnackbarClose = () => setSnackbarOpen(false);

  const handleEventClick = (clickInfo: EventClickArg) => {
    const domain = clickInfo.event.extendedProps?.domain as string | undefined;
    if (domain === 'tutor') {
      const raw = clickInfo.event.extendedProps?.rawEvent as TutorCalendarLessonEvent | undefined;
      if (raw) {
        setSelectedTutorEvent(enrichTutorEventRoomFromList(raw, rooms));
        setIsTutorEventDetailsOpen(true);
      }
      return;
    }

    const eventId = clickInfo.event.id;
    const event = calendarEvents.find(e => e.id === eventId);
    if (event) {
      setSelectedEvent(event);
      setIsEventDetailsOpen(true);
    }
  };

  const handleCloseTutorEventDetails = () => {
    setIsTutorEventDetailsOpen(false);
    setSelectedTutorEvent(null);
    setDeleteTutorBlockDialogOpen(false);
  };

  const handleDeleteTutorManualBlock = async () => {
    if (!selectedTutorEvent || selectedTutorEvent.kind !== 'manual') return;
    const tutorId = selectedTutorEvent.tutorId?.trim();
    const calendarEventId = selectedTutorEvent.calendarEventId?.trim();
    if (!tutorId || !calendarEventId) {
      setSnackbarSeverity('error');
      setSnackbarMessage(t('tutorCalendar.deleteBlockError', 'Failed to delete block'));
      setSnackbarOpen(true);
      return;
    }

    setDeletingTutorBlock(true);
    try {
      await hostApiClient.deleteHostTutorCalendarEvent(tutorId, calendarEventId);
      setDeleteTutorBlockDialogOpen(false);
      handleCloseTutorEventDetails();
      await fetchEvents();
      setSnackbarSeverity('success');
      setSnackbarMessage(t('tutorCalendar.deleteBlockSuccess', 'Block deleted'));
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Error deleting host tutor calendar event:', error);
      setSnackbarSeverity('error');
      setSnackbarMessage(t('tutorCalendar.deleteBlockError', 'Failed to delete block'));
      setSnackbarOpen(true);
    } finally {
      setDeletingTutorBlock(false);
    }
  };

  const handleCloseEventDetails = () => {
    setIsEventDetailsOpen(false);
    setSelectedEvent(null);
    setDeleteRoomBlockDialogOpen(false);
  };

  const handleDeleteRoomManualBlock = async () => {
    const roomEvent = selectedEvent as (CalendarEvent & {
      roomId?: string;
      isManualBlock?: boolean;
    }) | null;
    if (!roomEvent?.isManualBlock) return;

    const roomId = roomEvent.roomId?.trim();
    const rawId = roomEvent.id?.trim() || '';
    const calendarEventId = rawId.includes('_merged_') ? rawId.split('_merged_')[0] : rawId;
    if (!roomId || !calendarEventId) {
      setSnackbarSeverity('error');
      setSnackbarMessage(t('tutorCalendar.deleteBlockError', 'Failed to delete block'));
      setSnackbarOpen(true);
      return;
    }

    setDeletingRoomBlock(true);
    try {
      await hostApiClient.deleteHostRoomCalendarEvent(roomId, calendarEventId);
      setDeleteRoomBlockDialogOpen(false);
      handleCloseEventDetails();
      await fetchEvents();
      setSnackbarSeverity('success');
      setSnackbarMessage(t('tutorCalendar.deleteBlockSuccess', 'Block deleted'));
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Error deleting host room calendar event:', error);
      setSnackbarSeverity('error');
      setSnackbarMessage(t('tutorCalendar.deleteBlockError', 'Failed to delete block'));
      setSnackbarOpen(true);
    } finally {
      setDeletingRoomBlock(false);
    }
  };

  // Touch handlers for mobile swipe navigation
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe || isRightSwipe) {
      const calendarApi = calendarRef.current?.getApi();
      if (calendarApi) {
        if (isLeftSwipe) {
          // Swipe left - go to next period (next week/month)
          calendarApi.next();
        } else if (isRightSwipe) {
          // Swipe right - go to previous period (previous week/month)
          calendarApi.prev();
        }
      }
    }
  };

  // Check for booking conflicts and validate time
  const checkForConflicts = useCallback((roomId: string, startTime: Date, durationInMinutes: number): boolean => {
    if (!roomId) return false;
    
    const endTime = new Date(startTime.getTime() + durationInMinutes * 60000);
    
    return calendarEvents.some(event => {
      const eventRoomId = (event as any).roomId;
      if (eventRoomId !== roomId) return false;
      
      const eventStart = new Date(event.startTime + 'Z');
      const eventEnd = new Date(event.endTime + 'Z');
      
      return (startTime < eventEnd && endTime > eventStart);
    });
  }, [calendarEvents]);

  // Check if a booking time is in the past
  const isBookingInPast = (dateTime: Date): boolean => {
    const now = new Date();
    return dateTime < now;
  };

  // Keep host room duration aligned with the organization bookingSlotsConfig.
  useEffect(() => {
    if (createEventKind !== 'room') return;
    const options = buildDurationOptionsMinutes(organizationBookingSlotsConfig, 720);
    setBookingDuration((prev) => snapDurationToOptions(prev, options));
  }, [createEventKind, organizationBookingSlotsConfig]);

  // Update conflict status when selection changes
  useEffect(() => {
    if (!selectedDate || !selectedTime) {
      setHasConflict(false);
      setIsPastBooking(false);
      return;
    }

    const bookingDateTime = combineDateTime(selectedDate, selectedTime);
    const startsToCheck =
      createIntent === 'booking' ? [bookingDateTime, ...extraSessionStarts] : [bookingDateTime];

    if (createEventKind === 'room') {
      if (!selectedRoom) {
        setHasConflict(false);
        setIsPastBooking(false);
        return;
      }
      const toUtc = (start: Date) =>
        selectedSpace?.timezone ? zonedTimeToUtc(start, selectedSpace.timezone) : start;
      setIsPastBooking(startsToCheck.some((start) => isBookingInPast(toUtc(start))));
      setHasConflict(
        startsToCheck.some((start) => checkForConflicts(selectedRoom, toUtc(start), bookingDuration)),
      );
      return;
    }

    if (!actionTutorId) {
      setHasConflict(false);
      setIsPastBooking(false);
      return;
    }

    const timezone = resolveTutorActionTimezone();
    const durationMinutes =
      createEventKind === 'tutor' && createIntent === 'booking'
        ? tutorLessonOptions.find((option) => option.id === tutorLessonOptionId)?.durationMinutes ?? 60
        : bookingDuration;
    const toUtc = (start: Date) => (timezone ? zonedTimeToUtc(start, timezone) : start);
    setIsPastBooking(startsToCheck.some((start) => isBookingInPast(toUtc(start))));
    const tutorConflict = startsToCheck.some((start) => {
      const checkStart = toUtc(start);
      const checkEnd = new Date(checkStart.getTime() + durationMinutes * 60 * 1000);
      return tutorEvents.some((event) => {
        if (event.tutorId !== actionTutorId) return false;
        const eventStart = parseBookingCalendarInstant(event.startTime);
        const eventEnd = parseBookingCalendarInstant(event.endTime);
        return checkStart < eventEnd && checkEnd > eventStart;
      });
    });
    const roomConflict =
      createIntent === 'booking' && selectedRoom
        ? startsToCheck.some((start) => checkForConflicts(selectedRoom, toUtc(start), durationMinutes))
        : false;
    setHasConflict(tutorConflict || roomConflict);
  }, [
    actionTutor?.timezone,
    actionTutorId,
    bookingDuration,
    checkForConflicts,
    createEventKind,
    createIntent,
    extraSessionStarts,
    selectedDate,
    selectedRoom,
    selectedSpace?.timezone,
    selectedTime,
    tutorEvents,
    tutorLessonOptionId,
    tutorLessonOptions,
  ]);

  // Load available gears when room booking parameters change
  useEffect(() => {
    if (
      createIntent !== 'booking' ||
      createEventKind !== 'room' ||
      !selectedSpace ||
      !selectedRoom ||
      !selectedDate ||
      !selectedTime
    ) {
      if (createIntent !== 'booking' || createEventKind !== 'room') {
        setAvailableGears([]);
      }
      return;
    }

    const localDateTime = combineDateTime(selectedDate, selectedTime);
    const formattedUtcTime = formatTimeForApi(localDateTime, selectedSpace.timezone);

    setLoadingGears(true);
    setGearError(null);

    apiClient
      .getAvailableRoomGears(selectedRoom, formattedUtcTime, bookingDuration)
      .then((gears) => {
        setAvailableGears(gears);
        setSelectedAdditionalGears([]);
      })
      .catch((error) => {
        console.error(t('spaceCalendar.errorFetchingGears', 'Error fetching available gears:'), error);
        setGearError(t('spaceCalendar.errorLoadingGear'));
        setAvailableGears([]);
      })
      .finally(() => {
        setLoadingGears(false);
      });
  }, [
    createEventKind,
    createIntent,
    selectedSpace,
    selectedRoom,
    selectedDate,
    selectedTime,
    bookingDuration,
    t,
  ]);

  const onToggleGear = (gear: AdditionalGear) => {
    setSelectedAdditionalGears((prev) => {
      const exists = prev.some((s) => s.id === gear.id && s.source === gear.source);
      return exists ? prev.filter((s) => !(s.id === gear.id && s.source === gear.source)) : [...prev, { id: gear.id, source: gear.source }];
    });
  };

  const handleCreateBooking = async () => {
    if (createIntent === 'block') {
      if (createEventKind === 'tutor') {
        await handleCreateTutorBlockFromDialog();
        return;
      }
      await handleCreateRoomBlockFromDialog();
      return;
    }

    if (createEventKind === 'tutor') {
      await handleCreateTutorLessonFromDialog();
      return;
    }
    await handleCreateRoomBookingFromDialog();
  };

  const handleCreateRoomBlockFromDialog = async () => {
    if (!selectedDate || !selectedTime || !selectedRoom || !selectedSpace) {
      setSnackbarMessage(t('spaceCalendar.fillAllRequiredFields'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    const bookingDateTime = combineDateTime(selectedDate, selectedTime);
    const roomStartUtc = zonedTimeToUtc(bookingDateTime, selectedSpace.timezone);
    if (isBookingInPast(roomStartUtc)) {
      setSnackbarMessage(t('spaceCalendar.cannotBookInPast', 'Cannot book in the past'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }
    if (hasConflict) {
      setSnackbarMessage(t('spaceCalendar.timeSlotConflict'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    const formattedStartTime = formatTimeForApi(bookingDateTime, selectedSpace.timezone);
    const endDateTime = new Date(bookingDateTime.getTime() + bookingDuration * 60 * 1000);
    const formattedEndTime = formatTimeForApi(endDateTime, selectedSpace.timezone);
    const title = tutorTitle.trim() || t('spaceCalendar.block', 'Block');

    setSubmittingCreateEvent(true);
    try {
      const response = await hostApiClient.createHostRoomCalendarEvent(selectedRoom, {
        title,
        startTime: formattedStartTime,
        endTime: formattedEndTime,
      });
      void apiClient.trackHostRetentionEvent(HOST_RETENTION_EVENTS.CALENDAR_EVENT_CREATED, {
        calendarEventId: response.id,
        roomId: selectedRoom,
        roomName: rooms.find((room) => room.id === selectedRoom)?.name,
        spaceId: selectedSpace.id,
        spaceName: selectedSpace.name,
        title,
        startTime: formattedStartTime,
        endTime: formattedEndTime,
      });
      setSnackbarMessage(t('spaceCalendar.eventCreatedSuccess', 'Event created successfully'));
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setTutorTitle('');
      setIsBookingDialogOpen(false);
      await fetchEvents();
    } catch (error) {
      console.error(t('spaceCalendar.errorCreatingEvent', 'Error creating event:'), error);
      setSnackbarMessage(t('spaceCalendar.eventCreationError', 'Failed to create event'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setSubmittingCreateEvent(false);
    }
  };

  const handleCreateTutorBlockFromDialog = async () => {
    if (!actionTutorId || !selectedSpace || !selectedDate || !selectedTime) {
      setSnackbarMessage(t('spaceCalendar.fillAllRequiredFields'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    const startDateTime = combineDateTime(selectedDate, selectedTime);
    const timezone = resolveTutorActionTimezone() || selectedSpace.timezone;
    const startUtc = zonedTimeToUtc(startDateTime, timezone);
    if (isBookingInPast(startUtc)) {
      setSnackbarMessage(t('spaceCalendar.cannotBookInPast', 'Cannot book in the past'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }
    if (hasConflict) {
      setSnackbarMessage(
        t('tutorCalendar.slotConflictWarning', 'This slot conflicts with an existing lesson or block.'),
      );
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    const startTime = formatTimeForApi(startDateTime, timezone);
    const endDateTime = new Date(startDateTime.getTime() + bookingDuration * 60 * 1000);
    const endTime = formatTimeForApi(endDateTime, timezone);
    const title = tutorTitle.trim() || t('spaceCalendar.block', 'Block');

    setSubmittingCreateEvent(true);
    try {
      await hostApiClient.createHostTutorCalendarEvent(actionTutorId, {
        title,
        startTime,
        endTime,
      });
      setIsBookingDialogOpen(false);
      setTutorTitle('');
      setSnackbarMessage(t('spaceCalendar.tutorBlockCreatedSuccess', 'Tutor block created'));
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      await fetchEvents();
    } catch (error) {
      console.error('Error creating tutor block:', error);
      setSnackbarMessage(t('spaceCalendar.tutorBlockCreatedError', 'Failed to create tutor block'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setSubmittingCreateEvent(false);
    }
  };

  const handleCreateRoomBookingFromDialog = async () => {
    if (!selectedDate || !selectedTime || !selectedRoom || !selectedSpace) {
      setSnackbarMessage(t('spaceCalendar.fillAllRequiredFields'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    const bookingDateTime = combineDateTime(selectedDate, selectedTime);
    const wallStarts = [bookingDateTime, ...extraSessionStarts];
    if (wallStarts.some((start) => isBookingInPast(zonedTimeToUtc(start, selectedSpace.timezone)))) {
      setSnackbarMessage(t('spaceCalendar.cannotBookInPast', 'Cannot book in the past'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }
    if (hasConflict) {
      setSnackbarMessage(t('spaceCalendar.timeSlotConflict'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    if (
      bookingSessionsOverlap(wallStarts.map((start) => ({ start, durationMinutes: bookingDuration })))
    ) {
      setSnackbarMessage(
        t('booking.sessionsOverlap', "These sessions overlap. Choose times that don't run into each other."),
      );
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }
    const occurrences = buildRoomBookingOccurrences(wallStarts, bookingDuration, (start) =>
      formatTimeForApi(start, selectedSpace.timezone),
    );

    setSubmittingCreateEvent(true);
    try {
      const response = await hostApiClient.createManualRoomBooking({
        paymentMethod: PaymentMethod.CASH,
        roomId: selectedRoom,
        startTime: occurrences?.[0]?.startTime ?? formatTimeForApi(bookingDateTime, selectedSpace.timezone),
        durationInMinutes: bookingDuration,
        additionalGears: selectedAdditionalGears,
        regulationAccepted: true,
        ...(occurrences ? { occurrences } : {}),
        ...resolveManualGuestFields(),
      });
      showManualBookingCreatedSuccess(
        occurrences ? null : response.bookingLinkUrl,
        t('spaceCalendar.bookingCreatedSuccess'),
      );
      setIsBookingDialogOpen(false);
      await fetchEvents();
    } catch (error) {
      console.error(t('spaceCalendar.errorCreatingBooking', 'Error creating booking:'), error);
      setSnackbarMessage(
        getBookingSlotMismatchErrorMessage(
          error,
          t,
          'spaceCalendar.bookingCreationError',
          'Failed to create booking',
        ),
      );
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setSubmittingCreateEvent(false);
    }
  };

  const handleCreateTutorLessonFromDialog = async () => {
    const selectedLessonOption =
      tutorLessonOptions.find((option) => option.id === tutorLessonOptionId) ?? null;
    if (!actionTutorId || !selectedSpace || !selectedDate || !selectedTime || !selectedLessonOption?.id) {
      setSnackbarMessage(t('spaceCalendar.fillAllRequiredFields'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }
    const startDateTime = combineDateTime(selectedDate, selectedTime);
    const timezone = resolveTutorActionTimezone() || selectedSpace.timezone;
    const wallStarts = [startDateTime, ...extraSessionStarts];
    if (wallStarts.some((start) => isBookingInPast(zonedTimeToUtc(start, timezone)))) {
      setSnackbarMessage(t('spaceCalendar.cannotBookInPast', 'Cannot book in the past'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }
    if (hasConflict) {
      setSnackbarMessage(
        t('tutorCalendar.slotConflictWarning', 'This slot conflicts with an existing lesson or block.'),
      );
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    const lessonDuration = selectedLessonOption.durationMinutes ?? 60;
    if (bookingSessionsOverlap(wallStarts.map((start) => ({ start, durationMinutes: lessonDuration })))) {
      setSnackbarMessage(
        t('booking.sessionsOverlap', "These sessions overlap. Choose times that don't run into each other."),
      );
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }
    const serializedStarts = wallStarts.map((start) => formatTimeForApi(start, timezone));
    const startTime = serializedStarts[0];
    const occurrences = buildTutorBookingOccurrences(wallStarts, (start) => formatTimeForApi(start, timezone));
    const validationError = validateHostTutorWalkInBooking({
      tutorId: actionTutorId,
      lessonOptionId: selectedLessonOption.id,
      startTime,
      sessionType: selectedLessonOption.sessionType,
      roomId: selectedRoom,
    });
    if (validationError) {
      setSnackbarMessage(t('spaceCalendar.fillAllRequiredFields'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    setSubmittingCreateEvent(true);
    try {
      const guestFields = resolveManualGuestFields();
      const response = await hostApiClient.createManualTutorBooking(
        buildHostTutorWalkInBookingRequest({
          tutorId: actionTutorId,
          lessonOptionId: selectedLessonOption.id,
          startTime,
          occurrences,
          sessionType: selectedLessonOption.sessionType,
          roomId: selectedRoom,
          userId: guestFields.userId,
          phoneNumber: guestFields.phoneNumber,
          sendSms: guestFields.sendSms,
          paymentMethod: PaymentMethod.CASH,
        }),
      );
      setIsBookingDialogOpen(false);
      setTutorTitle('');
      setTutorStudentId(MANUAL_BOOKING_NEW_CLIENT);
      setGuestPhoneLocal('');
      setSendBookingLinkSms(true);
      showManualBookingCreatedSuccess(
        occurrences ? null : response.bookingLinkUrl,
        hasManualBookingClient(response.userId)
          ? t('spaceCalendar.tutorEventCreatedSuccess', 'Tutor lesson created')
          : t(
              'spaceCalendar.manualBookingNoClientAttached',
              'Lesson booked. No client account was attached.',
            ),
      );
      await fetchEvents();
    } catch (error) {
      console.error('Error creating tutor lesson:', error);
      setSnackbarMessage(
        getBookingSlotMismatchErrorMessage(
          error,
          t,
          'spaceCalendar.tutorEventCreatedError',
          'Failed to create tutor lesson',
        ),
      );
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setSubmittingCreateEvent(false);
    }
  };

  // Linked room copies of tutor lessons are not per-room colored, so the lesson
  // event is the one that stays on the grid.
  const linkedRoomCalendarEventIds = useMemo(
    () => collectTutorLessonRoomCalendarEventIds(tutorEvents),
    [tutorEvents],
  );

  const roomIdByCalendarEventId = useMemo(() => {
    const map = new Map<string, string>();
    for (const roomEvent of calendarEvents) {
      const calendarEventId = roomEvent.id.includes('_merged_')
        ? roomEvent.id.split('_merged_')[0]
        : roomEvent.id;
      const roomId = (roomEvent as { roomId?: string }).roomId?.trim();
      if (calendarEventId && roomId) {
        map.set(calendarEventId, roomId);
      }
    }
    return map;
  }, [calendarEvents]);

  // Format events for the calendar
  const formattedCalendarEvents = calendarEvents
    .filter((event) => {
      const roomId = (event as { roomId?: string }).roomId ?? '';
      if (!hostCalendarScopeShowsRoomEvent(calendarScope, roomId)) {
        return false;
      }
      return roomCalendarEventPassesDisplayRules(event, linkedRoomCalendarEventIds);
    })
    .map((event) => {
      if (!selectedSpace) return null;
      // Event times are in UTC from the backend (without the 'Z' suffix)
      const utcStart = new Date(event.startTime + 'Z');
      const utcEnd = new Date(event.endTime + 'Z');
      // Format time range for display in space timezone
      const title = event.title || t('spaceCalendar.booking');

      // Use generateRoomColor with roomId
      const roomId = (event as { roomId?: string }).roomId;
      const roomColor = generateRoomColor(roomId, rooms);
      const isManualBlock = Boolean((event as { isManualBlock?: boolean }).isManualBlock);
      const color = isManualBlock ? '#FFB648' : roomColor;
      const endWall = formatInTimeZone(utcEnd, selectedSpace.timezone, "yyyy-MM-dd'T'HH:mm:ss");
      return {
        id: event.id,
        title: title,
        start: formatInTimeZone(utcStart, selectedSpace.timezone, "yyyy-MM-dd'T'HH:mm:ss"),
        end: endWall,
        color,
        textColor: contrastTextForBackground(color),
        classNames: withPastEventClassNames(
          endWall,
          selectedSpace.timezone,
          isManualBlock ? ['manual-block'] : [],
        ),
        allDay: false,
        extendedProps: { domain: 'room' as const },
      };
    })
    .filter((event): event is NonNullable<typeof event> => event !== null);

  const roomColorById = useMemo(() => {
    const map = new Map<string, string>();
    rooms.forEach((room) => {
      map.set(room.id, generateRoomColor(room.id, rooms));
    });
    return map;
  }, [rooms]);

  const occupiedRoomColors = useMemo(
    () => Array.from(roomColorById.values()),
    [roomColorById],
  );

  const formattedTutorEvents = useMemo(() => {
    if (!selectedSpace) return [];

    return tutorEvents
      .flatMap((event) => {
        const enriched = enrichTutorEventRoomFromList(event, rooms);
        const roomId = tutorEventOccupiedRoomId(enriched, roomIdByCalendarEventId);
        if (
          !hostCalendarScopeShowsTutorEvent(calendarScope, {
            tutorId: enriched.tutorId,
            roomId,
          })
        ) {
          return [];
        }

        const utcStart = parseBookingCalendarInstant(enriched.startTime);
        const utcEnd = parseBookingCalendarInstant(enriched.endTime);
        const color = resolveHostTutorEventColor(enriched, spaceTutorOptions, occupiedRoomColors);
        const tutorTitle = formatHostUnifiedTutorEventTitle(enriched, {
          unnamedTutor: t('tutorCalendar.unnamedTutor', 'Tutor'),
          onlineLabel: t('spaceCalendar.onlineLesson', 'Online'),
          atStudentLabel: t('tutor.sessionType.atStudent', "At student's place"),
          unassignedRoomLabel: t('spaceCalendar.noRoomAssigned', 'No room'),
          rooms,
        });

        const endWall = formatInTimeZone(utcEnd, selectedSpace.timezone, "yyyy-MM-dd'T'HH:mm:ss");
        const lessonClasses = [tutorLessonClassName(enriched.kind)];

        return [
          {
            id: enriched.id,
            title: tutorTitle,
            start: formatInTimeZone(utcStart, selectedSpace.timezone, "yyyy-MM-dd'T'HH:mm:ss"),
            end: endWall,
            color,
            textColor: contrastTextForBackground(color),
            classNames: withPastEventClassNames(endWall, selectedSpace.timezone, lessonClasses),
            allDay: false,
            extendedProps: { domain: 'tutor' as const, rawEvent: enriched },
          },
        ];
      });
  }, [
    calendarScope,
    occupiedRoomColors,
    roomIdByCalendarEventId,
    rooms,
    selectedSpace,
    spaceTutorOptions,
    t,
    tutorEvents,
  ]);

  const dayHeaderDots = new Map<string, string[]>();
  formattedCalendarEvents.forEach((event) => {
    if (!event) return;
    pushDayHeaderDot(dayHeaderDots, event.start, event.color);
  });
  formattedTutorEvents.forEach((event) => {
    pushDayHeaderDot(dayHeaderDots, event.start, event.color);
  });

  // Combine all events including schedule indicators (working hours = white grid, no overlay)
  const formattedEvents = [
    ...workingHoursEvents,
    ...formattedCalendarEvents,
    ...formattedTutorEvents,
    ...blockedSlotEvents,
    ...pastBlockedEvents,
    ...dayOffEvents,
  ];

  const scopeActivity = useMemo(() => {
    const roomCounts = new Map<string, number>();
    const tutorCounts = new Map<string, number>();
    let allCount = 0;
    if (!selectedSpace) {
      return { roomCounts, tutorCounts, allCount };
    }

    const dayKeys =
      visibleDayKeys.length > 0
        ? new Set(visibleDayKeys)
        : new Set([formatInTimeZone(new Date(), selectedSpace.timezone, 'yyyy-MM-dd')]);

    const bump = (map: Map<string, number>, id: string | undefined) => {
      if (!id) return;
      map.set(id, (map.get(id) ?? 0) + 1);
    };

    calendarEvents.forEach((event) => {
      if (!roomCalendarEventPassesDisplayRules(event, linkedRoomCalendarEventIds)) return;
      const eventDay = formatInTimeZone(
        parseBookingCalendarInstant(event.startTime),
        selectedSpace.timezone,
        'yyyy-MM-dd',
      );
      if (!dayKeys.has(eventDay)) return;
      allCount += 1;
      bump(roomCounts, (event as { roomId?: string }).roomId?.trim());
    });

    tutorEvents.forEach((event) => {
      const eventDay = formatInTimeZone(
        parseBookingCalendarInstant(event.startTime),
        selectedSpace.timezone,
        'yyyy-MM-dd',
      );
      if (!dayKeys.has(eventDay)) return;
      allCount += 1;
      bump(tutorCounts, event.tutorId?.trim());
      bump(roomCounts, tutorEventOccupiedRoomId(event, roomIdByCalendarEventId));
    });

    return { roomCounts, tutorCounts, allCount };
  }, [
    calendarEvents,
    linkedRoomCalendarEventIds,
    roomIdByCalendarEventId,
    selectedSpace,
    tutorEvents,
    visibleDayKeys,
  ]);

  // App bar + fixed site footer clearance (see chatLayout / App Footer).
  const pageShellSx = {
    height: { xs: 'auto', md: 'calc(100dvh - 72px - 52px)' },
    minHeight: { xs: 'calc(100dvh - 56px - 52px)', md: 'calc(100dvh - 72px - 52px)' },
    display: 'flex',
    flexDirection: 'column',
    px: { xs: 1.5, sm: 2.5, lg: 3 },
    py: { xs: 1.5, md: 2 },
    boxSizing: 'border-box',
  };

  const calendarFrameSx = {
    flex: 1,
    minHeight: 0,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    bgcolor: 'transparent',
    border: 0,
    boxShadow: 'none',
    backgroundImage: 'none',
  };

  const scopeTutorCount = orgHasTutors ? spaceTutorOptions.length : 0;
  const showScopeBar = Boolean(selectedSpace) && hostCalendarScopeChooserVisible({
    roomCount: rooms.length,
    tutorCount: scopeTutorCount,
  });
  const colorEditor: {
    kind: 'room' | 'tutor';
    label: string;
    color: string;
    onChange: (color: string) => void;
  } | null = (() => {
    if (showScopeBar || !selectedSpace) return null;
    if (rooms.length === 1 && scopeTutorCount === 0) {
      const room = rooms[0];
      return {
        kind: 'room',
        label: room.name,
        color: roomColorById.get(room.id) ?? generateRoomColor(room.id, rooms),
        onChange: (color: string) => {
          void handleRoomColorChange(room.id, color);
        },
      };
    }
    if (rooms.length === 0 && scopeTutorCount === 1) {
      const tutor = spaceTutorOptions[0];
      return {
        kind: 'tutor',
        label: tutor.name,
        color: generateTutorColor(tutor.tutorId, spaceTutorOptions, occupiedRoomColors),
        onChange: (color: string) => {
          void handleTutorColorChange(tutor.tutorId, color);
        },
      };
    }
    return null;
  })();

  const scopeRoomChips = rooms.map((room) => ({
    id: room.id,
    label: room.name?.trim() || t('spaceCalendar.room', 'Room'),
    color: roomColorById.get(room.id) ?? generateRoomColor(room.id, rooms),
    count: scopeActivity.roomCounts.get(room.id) ?? 0,
    semanticsLabel: `${t('spaceCalendar.room', 'Room')}, ${room.name}`,
  }));
  const scopeTutorChips = orgHasTutors
    ? spaceTutorOptions.map((tutor) => ({
        id: tutor.tutorId,
        label: tutor.name,
        color: generateTutorColor(tutor.tutorId, spaceTutorOptions, occupiedRoomColors),
        count: scopeActivity.tutorCounts.get(tutor.tutorId) ?? 0,
        semanticsLabel: `${t('spaceCalendar.tutorLabel', 'Tutor')}, ${tutor.name}`,
      }))
    : [];

  const canCreate = Boolean(selectedSpace) && (rooms.length > 0 || scopeTutorCount > 0);
  const singleCalendarColorOptions = colorEditor
    ? withCurrentCalendarColor(
        colorEditor.kind === 'room' ? ROOM_CALENDAR_COLOR_OPTIONS : TUTOR_COLOR_OPTIONS,
        colorEditor.color,
      )
    : [];

  return (
    <Box sx={pageShellSx}>
      <Box
        sx={{
          ...calendarFrameSx,
          flex: 1,
          height: { xs: 'min(78vh, 720px)', md: '100%' },
          minHeight: 0,
        }}
      >
        <Box
          sx={{
            px: { xs: 1.5, sm: 2 },
            pt: 1.25,
            pb: calendarScope.kind === 'none' ? 0.5 : 1.25,
            flexShrink: 0,
          }}
        >
          <Stack
            direction="row"
            spacing={1}
            useFlexGap
            flexWrap="wrap"
            alignItems="flex-start"
          >
            {spaces.length > 1 ? (
              <FormControl size="small" sx={{ minWidth: 148, maxWidth: 220, flexShrink: 0 }}>
                <Select
                  aria-label={t('spaceCalendar.selectSpace', 'Space')}
                  value={selectedSpace?.id ?? ''}
                  onChange={(event) => handleSpaceChange(event.target.value)}
                  sx={{
                    height: 40,
                    borderRadius: '8px',
                    '& .MuiOutlinedInput-notchedOutline': { borderRadius: '8px' },
                  }}
                >
                  {spaces.map((space) => (
                    <MenuItem key={space.id} value={space.id}>
                      {space.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : null}

            {showScopeBar ? (
              <Box sx={{ flex: '1 1 220px', minWidth: 0, display: 'flex', alignItems: 'center' }}>
                <HostCalendarScopeBar
                  allLabel={t('spaceCalendar.scopeAll', 'All')}
                  allSemanticsLabel={t('spaceCalendar.scopeAllHint', 'Combined calendar')}
                  allCount={scopeActivity.allCount}
                  rooms={scopeRoomChips}
                  tutors={scopeTutorChips}
                  selected={calendarScope}
                  onSelected={setCalendarScope}
                  onRoomColorChange={handleRoomColorChange}
                  onTutorColorChange={orgHasTutors ? handleTutorColorChange : undefined}
                  chooserTitle={t('spaceCalendar.scopeChooserTitle', 'Calendars')}
                  chooserSubtitle={t(
                    'spaceCalendar.scopeChooserSubtitle',
                    'Choose which calendars to show',
                  )}
                  roomsLabel={t('spaceCalendar.roomsTitle', 'Rooms')}
                  tutorsLabel={t('spaceCalendar.tutorsTitle', 'Tutors')}
                  doneLabel={t('spaceCalendar.scopeDone', 'Done')}
                />
              </Box>
            ) : colorEditor ? (
              <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0, flex: '1 1 auto' }}>
                <CalendarColorPicker
                  color={colorEditor.color}
                  options={singleCalendarColorOptions}
                  onChange={colorEditor.onChange}
                  colorNamePrefix={
                    colorEditor.kind === 'tutor' ? 'spaceCalendar.tutorColorNames' : 'room.colorNames'
                  }
                  ariaLabel={
                    colorEditor.kind === 'room'
                      ? t('spaceCalendar.changeRoomColor', 'Change room color')
                      : t('spaceCalendar.changeTutorColor', 'Change tutor color')
                  }
                />
                <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
                  {colorEditor.label}
                </Typography>
              </Stack>
            ) : (
              <Box sx={{ flex: '1 1 auto' }} />
            )}

            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ ml: 'auto', flexShrink: 0 }}>
              <Button
                variant="contained"
                size="small"
                startIcon={<EventAvailableRoundedIcon />}
                onClick={() => openCreateEventDialog('booking', 'room')}
                disabled={!canCreate}
                sx={{ borderRadius: HOST_CALENDAR_CORNER_RADIUS, height: 40 }}
              >
                {t('spaceCalendar.createManualBooking', 'Create booking')}
              </Button>
              <Tooltip title={t('spaceCalendar.createEvent', 'Block Time')}>
                <span>
                  <IconButton
                    size="small"
                    aria-label={t('spaceCalendar.createEvent', 'Block Time')}
                    onClick={() => openCreateEventDialog('block', 'room')}
                    disabled={!canCreate}
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: HOST_CALENDAR_CORNER_RADIUS,
                      width: 40,
                      height: 40,
                    }}
                  >
                    <BlockRoundedIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              {selectedSpace ? (
                <Tooltip title={t('spaceCalendar.editSchedule', 'Edit working hours')}>
                  <IconButton
                    component={RouterLink}
                    to={`/host/spaces/${selectedSpace.id}#working-hours`}
                    size="small"
                    aria-label={t('spaceCalendar.editSchedule', 'Edit working hours')}
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: HOST_CALENDAR_CORNER_RADIUS,
                      width: 40,
                      height: 40,
                      color: 'text.primary',
                    }}
                  >
                    <EditCalendarRoundedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : null}
            </Stack>
          </Stack>
          {calendarScope.kind === 'none' ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75, px: 0.5 }}>
              {t(
                'spaceCalendar.scopeNone',
                'No calendars selected. Choose All or open the filter.',
              )}
            </Typography>
          ) : null}
        </Box>

        {spaces.length === 0 ? (
          <Box sx={{ flex: 1, display: 'grid', placeItems: 'center', p: 4 }}>
            <Stack spacing={1} alignItems="center" textAlign="center">
              <Typography variant="h6">{t('spaceCalendar.noSpaces', 'No spaces yet')}</Typography>
              <Typography variant="body2" color="text.secondary">
                {t('spaceCalendar.noSpacesHint', 'Create a space first, then add rooms to manage availability.')}
              </Typography>
            </Stack>
          </Box>
        ) : selectedSpace ? (
          <Box
            ref={calendarContainerRef}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            data-theme={mode}
            className="host-space-calendar"
            sx={{
              flexGrow: 1,
              width: '100%',
              overflow: 'hidden',
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              px: { xs: 1, sm: 1.5 },
              pb: { xs: 1.5, sm: 2 },
              ...tutorCalendarStyles,
              // Force FullCalendar root to stretch with the host layout when the nav collapses.
              '& .fc': {
                width: '100% !important',
                height: '100% !important',
                borderRadius: `${HOST_CALENDAR_CORNER_RADIUS} !important`,
              },
              '& .fc-view-harness, & .fc-scrollgrid, & .fc-timegrid': {
                width: '100% !important',
              },
              '& .fc-col-header-cell-cushion': {
                backgroundColor: 'transparent !important',
                color: '#1f1f1f !important',
                borderBottom: '1px solid rgba(0, 0, 0, 0.08) !important',
              },
              '& .fc-col-header-cell:nth-child(1) .fc-col-header-cell-cushion': {
                backgroundColor: 'transparent !important',
                color: '#1f1f1f !important',
                borderBottom: '1px solid rgba(0, 0, 0, 0.08) !important',
              },
              '& .fc-timegrid-axis': {
                width: '50px !important',
                minWidth: '50px !important',
                maxWidth: '50px !important',
                backgroundColor: 'transparent !important',
                borderRight: '1px solid rgba(0, 0, 0, 0.08) !important',
              },
              '& .fc-timegrid-slot-label-cushion': {
                color: 'rgba(0, 0, 0, 0.55) !important',
              },
              '& .fc-monthYearTitle-button': {
                color: '#1f1f1f !important',
                background: 'transparent !important',
                backgroundColor: 'transparent !important',
                border: 'none !important',
                boxShadow: 'none !important',
                borderRadius: '0 !important',
                padding: '0 !important',
                minHeight: '0 !important',
              },
              '& .fc-button:hover:not(.fc-monthYearTitle-button)': {
                backgroundColor: 'rgba(78, 103, 235, 0.08) !important',
                borderColor: `${HOST_CALENDAR_ACCENT} !important`,
                color: `${HOST_CALENDAR_ACCENT} !important`,
              },
              '& .fc-button.fc-button-active': {
                backgroundColor: `${HOST_CALENDAR_ACCENT} !important`,
                borderColor: `${HOST_CALENDAR_ACCENT} !important`,
                color: '#ffffff !important',
              },
            }}
          >
            <style>{sharedCalendarCompactStyles}</style>
            <style>{calendarOverlayCompactStyles}</style>
            <style>{tutorCalendarCompactStyles}</style>
            <style>{`
              /* sharedCalendarCompactStyles hides the built-in title — keep our month/year button visible */
              .fc-toolbar-title { display: none !important; }
              .host-space-calendar .fc-monthYearTitle-button {
                display: inline-flex !important;
                visibility: visible !important;
                opacity: 1 !important;
                color: #1f1f1f !important;
                font-size: 1.05rem !important;
                font-weight: 600 !important;
                text-transform: none !important;
                background: transparent !important;
                background-color: transparent !important;
                border: none !important;
                outline: none !important;
                box-shadow: none !important;
                border-radius: 0 !important;
                padding: 0 !important;
                min-height: 0 !important;
                pointer-events: none !important;
                cursor: default !important;
              }
              /* Vertical day separators — drawn as overlays (slot table is full-width) */
              .fc .fc-timegrid-cols {
                z-index: 3 !important;
              }
              .fc .fc-timegrid-col {
                position: relative !important;
              }
              .fc .fc-timegrid-col:not(:last-child)::after {
                content: '' !important;
                position: absolute !important;
                top: 0 !important;
                right: 0 !important;
                bottom: 0 !important;
                width: 1px !important;
                background: rgba(0, 0, 0, 0.1) !important;
                pointer-events: none !important;
                z-index: 6 !important;
              }
              .fc .fc-col-header-cell {
                position: relative !important;
              }
              .fc .fc-col-header-cell:not(:last-child)::after {
                content: '' !important;
                position: absolute !important;
                top: 0 !important;
                right: 0 !important;
                bottom: 0 !important;
                width: 1px !important;
                background: rgba(0, 0, 0, 0.1) !important;
                pointer-events: none !important;
                z-index: 6 !important;
              }
              .fc-timegrid-axis,
              .fc-timegrid-axis-frame,
              .fc-timegrid-slot-label,
              .fc-timegrid-axis-cushion {
                border-right: none !important;
              }
              .host-space-calendar .fc {
                border-radius: ${HOST_CALENDAR_CORNER_RADIUS} !important;
              }
              .host-space-calendar .fc .fc-col-header-cell {
                height: auto !important;
                min-height: 48px !important;
                max-height: none !important;
                padding: 8px 4px 6px !important;
              }
              .host-space-calendar .fc .fc-col-header-cell-cushion {
                display: flex !important;
                justify-content: center !important;
                padding: 0 !important;
              }
              .host-cal-day {
                display: flex;
                flex-direction: column;
                align-items: center;
                gap: 4px;
                line-height: 1.15;
              }
              .host-cal-day.is-today {
                color: ${HOST_CALENDAR_ACCENT};
                font-weight: 700;
              }
              .host-cal-dots {
                display: flex;
                gap: 3px;
                min-height: 6px;
                align-items: center;
                justify-content: center;
              }
              .host-cal-dots i {
                width: 6px;
                height: 6px;
                border-radius: 50%;
                display: block;
              }
              .host-space-calendar .fc .fc-timegrid-col-events {
                margin: 0 2px !important;
              }
              .host-space-calendar .fc-dayGridMonth-button,
              .host-space-calendar .fc-timeGridWeek-button,
              .host-space-calendar .fc-timeGridDay-button,
              .host-space-calendar .fc-prev-button,
              .host-space-calendar .fc-next-button,
              .host-space-calendar .fc-today-button {
                border: 1px solid rgba(31, 33, 41, 0.28) !important;
                background-color: #fff !important;
              }
              .host-space-calendar .fc-prev-button,
              .host-space-calendar .fc-next-button,
              .host-space-calendar .fc-today-button,
              .host-space-calendar .fc-prev-button .fc-icon,
              .host-space-calendar .fc-next-button .fc-icon,
              .host-space-calendar .fc-today-button:disabled {
                color: #1F2129 !important;
                opacity: 1 !important;
              }
              .host-space-calendar .fc-dayGridMonth-button.fc-button-active,
              .host-space-calendar .fc-timeGridWeek-button.fc-button-active,
              .host-space-calendar .fc-timeGridDay-button.fc-button-active {
                border-color: ${HOST_CALENDAR_ACCENT} !important;
                background-color: ${HOST_CALENDAR_ACCENT} !important;
                color: #fff !important;
              }
              .host-space-calendar .fc-event.tutor-lesson-confirmed,
              .host-space-calendar .fc-event.tutor-lesson-pending,
              .host-space-calendar .fc-event.tutor-lesson-manual,
              .host-space-calendar .fc-event.manual-block {
                box-shadow: none !important;
              }
            `}</style>
            <FullCalendar
              key={`host-cal-${selectedSpace.id}-${timeRange.slotMinTime}-${timeRange.slotMaxTime}`}
              ref={calendarRef}
              plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
              initialView={view}
              firstDay={1}
              dayHeaders={true}
              events={formattedEvents}
              headerToolbar={{
                left: 'dayGridMonth,timeGridWeek,timeGridDay',
                center: 'monthYearTitle',
                right: 'prev,next today',
              }}
              customButtons={{
                monthYearTitle: {
                  text: toolbarMonthYearLabel,
                  click: function () {
                    // display only
                  },
                },
              }}
              buttonText={{
                today: t('calendar.today', 'Today'),
                month: t('calendar.month', 'Month'),
                week: t('calendar.week', 'Week'),
                day: t('calendar.day', 'Day'),
              }}
              navLinks={true}
              titleFormat={{
                month: 'long',
                year: 'numeric',
              }}
              timeZone={selectedSpace.timezone}
              nowIndicator
              displayEventTime={true}
              eventTimeFormat={{
                hour: '2-digit',
                minute: '2-digit',
                hour12: false,
              }}
              slotMinTime={timeRange.slotMinTime}
              slotMaxTime={timeRange.slotMaxTime}
              scrollTime={timeRange.slotMinTime}
              slotDuration="00:30:00"
              views={{
                dayGridMonth: {
                  displayEventTime: true,
                  dayMaxEvents: 3,
                  fixedWeekCount: false,
                  dayHeaderFormat: { weekday: 'short' },
                },
                timeGridDay: {
                  displayEventTime: true,
                  slotDuration: '00:30:00',
                  slotMinTime: timeRange.slotMinTime,
                  slotMaxTime: timeRange.slotMaxTime,
                  dayHeaderFormat: {
                    day: 'numeric',
                    weekday: 'short',
                  },
                  allDaySlot: false,
                },
                timeGridWeek: {
                  displayEventTime: true,
                  slotDuration: '00:30:00',
                  slotMinTime: timeRange.slotMinTime,
                  slotMaxTime: timeRange.slotMaxTime,
                  dayHeaderFormat: {
                    day: 'numeric',
                    weekday: 'short',
                  },
                  allDaySlot: false,
                },
              }}
              allDaySlot={false}
              selectable={view !== 'dayGridMonth'}
              select={handleDateSelect}
              dayMaxEvents={false}
              dateClick={handleDateClick}
              selectMirror={true}
              unselectAuto={false}
              selectMinDistance={1}
              locales={allLocales}
              locale={getFullCalendarLocale(t('calendar.locale', 'en'))}
              dayHeaderFormat={{
                weekday: 'short',
                day: '2-digit',
              }}
              dayHeaderContent={(arg) => {
                const showDots = arg.view.type !== 'dayGridMonth';
                const dots = showDots ? dayHeaderDots.get(fullCalendarHeaderDayKey(arg.date)) ?? [] : [];
                return (
                  <span className={arg.isToday ? 'host-cal-day is-today' : 'host-cal-day'}>
                    <span>{arg.text}</span>
                    {showDots ? (
                      <span className="host-cal-dots" aria-hidden="true">
                        {dots.map((color, index) => (
                          <i key={`${color}-${index}`} style={{ backgroundColor: color }} />
                        ))}
                      </span>
                    ) : null}
                  </span>
                );
              }}
              expandRows={true}
              handleWindowResize={true}
              height="100%"
              datesSet={handleDatesSet}
              eventDidMount={(info) => mountPastEventClass(info, selectedSpace.timezone)}
              eventClick={handleEventClick}
              editable={false}
              droppable={false}
              slotLabelFormat={{
                hour: '2-digit',
                minute: '2-digit',
                hour12: false,
              }}
            />
          </Box>
        ) : null}
      </Box>

      {/* Event Details Dialog */}
      <RoomCalendarEventDetailsDialog
        open={isEventDetailsOpen}
        event={selectedEvent as (CalendarEvent & {
          roomId?: string;
          roomName?: string;
          isManualBlock?: boolean;
        }) | null}
        onClose={handleCloseEventDetails}
        canDeleteBlock={Boolean(
          (selectedEvent as { isManualBlock?: boolean } | null)?.isManualBlock &&
            (selectedEvent as { roomId?: string } | null)?.roomId &&
            selectedEvent?.id,
        )}
        deletingBlock={deletingRoomBlock}
        onRequestDeleteBlock={() => setDeleteRoomBlockDialogOpen(true)}
      />

      <Dialog
        open={deleteRoomBlockDialogOpen}
        onClose={() => !deletingRoomBlock && setDeleteRoomBlockDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t('tutorCalendar.deleteBlockTitle', 'Delete this block?')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {t(
              'spaceCalendar.deleteBlockConfirm',
              'This removes the manual block from the room calendar. This cannot be undone.',
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteRoomBlockDialogOpen(false)} disabled={deletingRoomBlock}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            onClick={() => {
              void handleDeleteRoomManualBlock();
            }}
            color="error"
            variant="contained"
            disabled={deletingRoomBlock}
          >
            {deletingRoomBlock
              ? t('common.loading', 'Loading...')
              : t('tutorCalendar.deleteBlock', 'Delete block')}
          </Button>
        </DialogActions>
      </Dialog>

    {/* Create Event / Manual Booking Dialog */}
    <BookingDialog
      open={isBookingDialogOpen}
      onClose={handleCloseBookingDialog}
      intent={createIntent}
      eventKind={createEventKind}
      setEventKind={handleCreateEventKindChange}
      rooms={rooms}
      tutors={spaceTutorOptions}
      selectedTutorId={actionTutorId}
      setSelectedTutorId={handleActionTutorChange}
      students={organizationStudents}
      studentsLoading={organizationStudentsLoading}
      selectedStudentId={tutorStudentId}
      setSelectedStudentId={setTutorStudentId}
      guestPhoneLocal={guestPhoneLocal}
      setGuestPhoneLocal={setGuestPhoneLocal}
      sendBookingLinkSms={sendBookingLinkSms}
      setSendBookingLinkSms={setSendBookingLinkSms}
      tutorLessonOptions={tutorLessonOptions}
      organizationBookingSlotsConfig={organizationBookingSlotsConfig}
      tutorBookingSlotsConfig={actionTutor?.bookingSlotsConfig}
      currencyCode={selectedSpace?.currencyCode ? String(selectedSpace.currencyCode) : 'PLN'}
      discountSettings={organizationDiscountSettings}
      tutorLessonOptionId={tutorLessonOptionId}
      setTutorLessonOptionId={setTutorLessonOptionId}
      tutorTitle={tutorTitle}
      setTutorTitle={setTutorTitle}
      selectedDate={selectedDate}
      setSelectedDate={setSelectedDate}
      selectedTime={selectedTime}
      setSelectedTime={setSelectedTime}
      extraSessionStarts={extraSessionStarts}
      setExtraSessionStarts={setExtraSessionStarts}
      selectedRoom={selectedRoom}
      setSelectedRoom={setSelectedRoom}
      bookingDuration={bookingDuration}
      setBookingDuration={setBookingDuration}
      isPastBooking={isPastBooking}
      hasConflict={hasConflict}
      submitting={submittingCreateEvent}
      handleCreateBooking={handleCreateBooking}
      availableGears={availableGears}
      selectedAdditionalGears={selectedAdditionalGears}
      loadingGears={loadingGears}
      gearError={gearError}
      onToggleGear={onToggleGear}
    />

    <TutorCalendarEventDetailsDialog
      open={isTutorEventDetailsOpen}
      event={selectedTutorEvent}
      audience="host"
      onClose={handleCloseTutorEventDetails}
      canDeleteBlock={Boolean(
        selectedTutorEvent?.kind === 'manual' &&
          selectedTutorEvent.calendarEventId &&
          selectedTutorEvent.tutorId,
      )}
      deletingBlock={deletingTutorBlock}
      onRequestDeleteBlock={() => setDeleteTutorBlockDialogOpen(true)}
      rooms={rooms}
      timezone={selectedSpace?.timezone}
    />

    <Dialog
      open={deleteTutorBlockDialogOpen}
      onClose={() => !deletingTutorBlock && setDeleteTutorBlockDialogOpen(false)}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>{t('tutorCalendar.deleteBlockTitle', 'Delete this block?')}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary">
          {t(
            'tutorCalendar.deleteBlockConfirm',
            'This removes the manual block from the tutor calendar. This cannot be undone.',
          )}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setDeleteTutorBlockDialogOpen(false)} disabled={deletingTutorBlock}>
          {t('common.cancel', 'Cancel')}
        </Button>
        <Button
          onClick={() => {
            void handleDeleteTutorManualBlock();
          }}
          color="error"
          variant="contained"
          disabled={deletingTutorBlock}
        >
          {deletingTutorBlock
            ? t('common.loading', 'Loading...')
            : t('tutorCalendar.deleteBlock', 'Delete block')}
        </Button>
      </DialogActions>
    </Dialog>

    {/* Snackbar for notifications */}
      <ManualBookingLinkDialog
        open={bookingLinkDialogOpen}
        bookingLinkUrl={createdBookingLinkUrl}
        copied={bookingLinkCopied}
        onCopy={() => {
          void handleCopyBookingLink();
        }}
        onClose={() => setBookingLinkDialogOpen(false)}
      />

      <Snackbar 
        open={snackbarOpen} 
        autoHideDuration={6000} 
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{
          '& .MuiSnackbar-root': {
            bottom: { xs: 16, sm: 24 }
          }
        }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}
